// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from '@stencil/core'
import {
  AttachInternals,
  Component,
  Element,
  Event,
  h,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import type {
  Attributes,
  QdsFocusEventDetail,
  QdsInputEventDetail,
} from '../../helpers'
import {
  inheritAriaAttributes,
  invariant,
  pickFocusEventAttributes,
  pickInputEventAttributes,
} from '../../helpers'
import type { AutoComplete, EnterKeyHint, InputMode, Value } from '../controls'
import { CUSTOM_ERROR_FLAGS, NO_ERROR_FLAGS, VALID_STATE } from '../controls'
import type { Size } from '../shared'

export type Capture = '' | 'environment' | 'user'

export type InputType =
  | 'color'
  | 'date'
  | 'datetime-local'
  | 'email'
  | 'file'
  | 'month'
  | 'number'
  | 'password'
  | 'search'
  | 'tel'
  | 'text'
  | 'time'
  | 'url'
  | 'week'

const stopImmediatePropagation = (event: Event): void => {
  event.stopImmediatePropagation()
}
const supportsSearchEvent = (): boolean => {
  const input = document.createElement('input')
  input.type = 'search'
  return input.onsearch !== undefined
}

/**
 * The `<qds-input>` element is used to create interactive controls for
 * web-based forms in order to accept data from the user; a wide variety of
 * types of input data and control widgets are available, depending on the
 * device and
 * [user agent](https://developer.mozilla.org/docs/Glossary/User_agent).
 *
 * @see https://quartz.se.com/build/components/input
 */
@Component({
  tag: 'qds-input',
  formAssociated: true,
  shadow: { delegatesFocus: true },
  styleUrl: 'input.css',
})
export class Input implements ComponentInterface {
  /**
   * Hide the input type's icon.
   *
   * > **_NOTE:_** Due to browser limitations, the icon may not be hidden in
   * all browsers.
   */
  @Prop() public readonly noIcon?: boolean = false

  /**
   * The input's size.
   *
   * > **_NOTE:_** The native [`size` HTML attribute][size] is not supported.
   * CSS `width` should be used instead if this functionality is needed.
   *
   * [size]: https://developer.mozilla.org/docs/Web/HTML/Attributes/size
   */
  @Prop() public readonly size?: Size = 'standard'

  /**
   * Defines the file types the `file` type input should accept. This is a
   * comma-separated list of [unique file type specifiers][]. Because a given
   * file type may be identified in more than one manner, it's useful to
   * provide a thorough set of type specifiers when you files of a given format
   * are needed.
   *
   * For instance, there are a number of ways Microsoft Word files can be
   * identified, so a site that accepts Word files might use a `<qds-input>`
   * like this:
   *
   * @example
   * <qds-input
   *   type="file"
   *   accept=".doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
   * ></qds-input>
   *
   * [unique file type specifiers]: https://developer.mozilla.org/docs/Web/HTML/Element/input/file#unique_file_type_specifiers
   *
   * @webnative
   */
  @Prop() public readonly accept?: string

  /**
   * Provides a hint for a [user agent's][] autocomplete feature. See
   * [the HTML `autocomplete` attribute][autocomplete] for a complete list of
   * values and details on how to use `autoComplete`.
   *
   * [user agent's]: https://developer.mozilla.org/docs/Glossary/User_agent
   * [autocomplete]: https://developer.mozilla.org/docs/Web/HTML/Attributes/autocomplete
   *
   * @webnative
   */
  @Prop({ attribute: 'autocomplete' })
  public readonly autoComplete?: AutoComplete

  /**
   * Specifies that a new file should be captured and which device should be
   * used to capture that new media of the type defined by `accept`.
   *
   * Values include `user`, `environment`, and `''`:
   *
   * - `user`: The user-facing camera and/or microphone should be used.
   * - `environment`: The outward-facing camera and/or microphone should be
   * used.
   * - `''`: The implementation-specific capture state.
   *
   * @webnative
   */
  @Prop() public readonly capture?: Capture

  /**
   * Prevents the input from being interacted with: it cannot be pressed or
   * focused.
   *
   * @webnative
   */
  @Prop() public readonly disabled?: boolean

  /**
   * What action label (or icon) to present for the enter key on virtual
   * keyboards. Possible values:
   *
   * - `"done"`: Typically meaning there is nothing more to input and the input
   * method editor (IME) will be closed.
   * - `"enter"`:	Typically inserting a new line.
   * - `"go"`: 	Typically meaning to take the user to the target of the text
   * they typed.
   * - `"next"`: 	Typically taking the user to the next field that will accept
   * text.
   * - `"previous"`: Typically taking the user to the previous field that will
   * accept text.
   * - `"search"`: Typically taking the user to the results of searching for
   * the text they have typed.
   * - `"send"`: Typically delivering the text to its target.
   *
   * @webnative
   */
  // FIXME: Can't use `attribute` option here to name this `enterKeyHint` instead.
  @Prop() public readonly enterkeyhint?: EnterKeyHint

  /**
   * Returns a file list for a `file` type input.
   *
   * @readonly
   * @webnative
   */
  // eslint-disable-next-line unicorn/no-null
  @Prop() public readonly files: HTMLInputElement['files'] = null

  /**
   * The [`<form>`][] element to associate the input with (its form owner).
   *
   * The value of this attribute must be the id of a `<form>` in the same
   * document. If this attribute is not set, the `<qds-input>` is associated
   * with its ancestor `<form>` element, if any.
   *
   * This attribute lets you associate `<qds-input>` elements to `<form>`s
   * anywhere in the document, not just inside a `<form>`. It can also override
   * an ancestor `<form>` element.
   *
   * [`<form>`]: https://developer.mozilla.org/docs/Web/HTML/Element/form
   *
   * @readonly
   * @webnative
   */
  // eslint-disable-next-line unicorn/no-null
  @Prop() public readonly form: ElementInternals['form'] | string = null

  /**
   * Provide a hint to browsers as to the type of virtual keyboard
   * configuration to use when editing this element or its contents.
   *
   * @webnative
   */
  // FIXME: Can't use `attribute` option here to name this `inputMode` instead.
  @Prop() public readonly inputmode?: InputMode

  /**
   * Returns a list of the [`<label>`][] elements associated with the
   * `qds-input` element.
   *
   * [`<label>`]: https://developer.mozilla.org/docs/Web/HTML/Element/label
   *
   * @readonly
   * @webnative
   */
  @Prop() public readonly labels: ElementInternals['labels'] =
    {} as ElementInternals['labels']

  /**
   * Defines the greatest value in the range of permitted values. If the
   * `value` entered into the input exceeds this, the input fails
   * [constraint validation][]. Only valid for `date`, `datetime-local`,
   * `month`, `time`, and `week` inputs.
   *
   * If this value isn't a valid number, then the input has no maximum value.
   *
   * [constraint validation]: https://developer.mozilla.org/docs/Web/Guide/HTML/Constraint_validation
   *
   * @webnative
   */
  @Prop() public readonly max?: number | string

  /**
   * The maximum number of characters (as UTF-16 code units) the user can enter
   * into the input. This must be an non-negative integer value. If no
   * `maxlength` is specified, or an invalid value is specified, the input has
   * no maximum length. Only valid for `email`, `password`, `search`, `tel`,
   * `text`, and `url` inputs.
   *
   * The input will fail [constraint validation][] if the length of the text
   * entered into the field is greater than `maxlength` UTF-16 code units long.
   * By default, browsers prevent users from entering more characters than
   * allowed by the `maxlength` attribute. See [Client-side validation][] for
   * more information.
   *
   * [constraint validation]: https://developer.mozilla.org/docs/Web/Guide/HTML/Constraint_validation
   * [Client-side validation]: https://developer.mozilla.org/docs/Web/HTML/Element/input#client-side_validation
   *
   * @webnative
   */
  @Prop({ attribute: 'maxlength' }) public readonly maxLength?: number

  /**
   * Defines the most negative value in the range of permitted values. If the
   * `value` entered into the input is less than this, the input fails
   * [constraint validation][]. Only valid for `date`, `datetime-local`,
   * `month`, `time`, and `week` inputs.
   *
   * This value must be less than or equal to the value of the `max` attribute.
   * If this value isn't a valid number, then the input has no minimum value.
   *
   * [constraint validation]: https://developer.mozilla.org/docs/Web/Guide/HTML/Constraint_validation
   *
   * @webnative
   */
  @Prop() public readonly min?: number | string

  /**
   * The minimum number of characters (as UTF-16 code units) the user can enter
   * into the input. This must be an non-negative integer value smaller than or
   * equal to the value specified by `maxlength`. If no `minlength` is
   * specified, or an invalid value is specified, the input has no minimum
   * length. Only valid for `email`, `password`, `search`, `tel`, `text`, and
   * `url` inputs.
   *
   * The input will fail [constraint validation][] if the length of the text
   * entered into the field is fewer than `minlength` UTF-16 code units long,
   * preventing form submission. See [Client-side validation][] for more
   * information.
   *
   * [constraint validation]: https://developer.mozilla.org/docs/Web/Guide/HTML/Constraint_validation
   * [Client-side validation]: https://developer.mozilla.org/docs/Web/HTML/Element/input#client-side_validation
   *
   * @webnative
   */
  @Prop({ attribute: 'minlength' }) public readonly minLength?: number

  /**
   * For `email` inputs, specifies if multiple comma-separated email addresses
   * can be entered. For `file` inputs, specifies if the user is allowed to
   * select more than one file.
   *
   * Only valid for `email` or `file` inputs.
   *
   * @webnative
   */
  @Prop() public readonly multiple?: boolean

  /**
   * The name of the input, which is submitted with the form data.
   *
   * @webnative
   */
  @Prop() public readonly name?: string

  /**
   * Defines a regular expression that the input's `value` must match in order
   * for the value to pass [constraint validation][]. It must be a valid
   * JavaScript regular expression, as used by the [`RegExp`][] type, and as
   * documented in MDN's [guide on regular expressions][]; the `'u'` flag is
   * specified when compiling the regular expression, so that the pattern is
   * treated as a sequence of Unicode code points, instead of as ASCII. No
   * forward slashes should be specified around the pattern text. Only valid
   * for `email`, `password`, `search`, `tel`, `text`, and `url` inputs.
   *
   * If the `pattern` is invalid, no regular expression is applied and this
   * property is ignored. If the pattern is valid and a non-empty value does
   * not match the pattern, constraint validation will prevent form submission.
   *
   * [constraint validation]: https://developer.mozilla.org/docs/Web/Guide/HTML/Constraint_validation
   * [`RegExp`]: https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/RegExp
   * [guide on regular expressions]: https://developer.mozilla.org/docs/Web/JavaScript/Guide/Regular_Expressions
   *
   * @webnative
   */
  @Prop() public readonly pattern?: string

  /**
   * Text that appears in the input when it has no value set. Only valid for
   * `email`, `number`, `password`, `search`, `tel`, `text`, and `url` inputs.
   *
   * @webnative
   */
  @Prop() public readonly placeholder?: string

  /**
   * A value must be specified for the input before the owning form can be
   * submitted. Not valid for `color` inputs.
   *
   * See [Client-side validation][] and the
   * [HTML attribute: `required`][required] for more information.
   *
   * [Client-side validation]: https://developer.mozilla.org/docs/Web/HTML/Element/input#client-side_validation
   * [required]: https://developer.mozilla.org/docs/Web/HTML/Attributes/required
   *
   * @webnative
   */
  @Prop() public readonly required?: boolean

  /**
   * Get or set the selection direction of a text selection.
   *
   * > **_NOTE:_** This property has no effect when used as an attribute.
   *
   * @webnative
   */
  @Prop({ mutable: true })
  // eslint-disable-next-line unicorn/no-null
  public selectionDirection: HTMLInputElement['selectionDirection'] = null

  /**
   * Get or set the ending position or offset of a text selection.
   *
   * > **_NOTE:_** This property has no effect when used as an attribute.
   *
   * @webnative
   */
  @Prop({ mutable: true })
  // eslint-disable-next-line unicorn/no-null
  public selectionEnd: HTMLInputElement['selectionEnd'] = null

  /**
   * Get or set the starting position or offset of a text selection.
   *
   * > **_NOTE:_** This property has no effect when used as an attribute.
   *
   * @webnative
   */
  @Prop({ mutable: true })
  // eslint-disable-next-line unicorn/no-null
  public selectionStart: HTMLInputElement['selectionStart'] = null

  /**
   * Specifies the granularity that the `value` must adhere to. Only valid for
   * `date`, `datetime-local`, `month`, `number`, `time`, and `week` inputs.
   *
   * The value must be a positive number—integer or float—or the special value
   * `any`, which means no stepping is implied, and any value is allowed
   * (barring other constraints, such as `min` and `max`).
   *
   * If `any` is not explicitly set, valid values for the `number` are the
   * basis for stepping — the `min` value and increments of the step value, up
   * to the `max` value, if specified.
   *
   * @webnative
   */
  @Prop() public readonly step?: number | 'any'

  /**
   * The type of input to render:
   *
   * - `"date"`: An input for entering a date (year, month, and day, with no
   * time). Opens a date picker or numeric wheels for year, month, day when
   * active in supporting browsers.
   * - `"datetime-local"`: An input for entering a date and time, with no time
   * zone. Opens a date picker or numeric wheels for date and time components
   * when active in supporting browsers.
   * - `"email"`: An input for editing an email address. Looks like a `"text"`
   * input, but has validation parameters and relevant keyboard in supporting
   * browsers and devices with dynamic keyboards.
   * - `"month"`: An input for entering a month and year, with no time zone.
   * - `"number"`: An input for entering a number. Displays a spinner and adds
   * default validation when supported. Displays a numeric keypad in some
   * devices with dynamic keypads.
   * - `"password"`: A single-line text input whose value is obscured. Will
   * alert user if site is not secure.
   * - `"search"`: A single-line text input for entering search strings.
   * Line-breaks are automatically removed from the input value. May include a
   * delete icon in supporting browsers that can be used to clear the input.
   * Displays a search icon instead of enter key on some devices with dynamic
   * keypads.
   * - `"tel"`: An input for entering a telephone number. Displays a telephone
   * keypad in some devices with dynamic keypads.
   * - `"text"`: A single-line text input. Line-breaks are automatically
   * removed from the input value.
   * - `"time"`: An input for entering a time value with no time zone.
   * - `"url"`: An input for entering a URL. Looks like a `"text"` input, but
   * has validation parameters and relevant keyboard in supporting browsers and
   * devices with dynamic keyboards.
   * - `"week"`: An input for entering a date consisting of a week-year number
   * and a week number with no time zone.
   *
   * @webnative
   */
  @Prop() public readonly type?: InputType

  /**
   * The error message that would be shown to the user if the `<qds-input>`
   * was to be checked for validity.
   *
   * @readonly
   * @webnative
   */
  @Prop()
  public readonly validationMessage: ElementInternals['validationMessage'] = ''

  /**
   * The [`ValidityState`][] object for this `<qds-input>`.
   *
   * [`ValidityState`]: https://developer.mozilla.org/docs/Web/API/ValidityState
   *
   * @readonly
   * @webnative
   */
  @Prop() public readonly validity: ElementInternals['validity'] = VALID_STATE

  /**
   * The value of the input.
   *
   * @webnative
   */
  @Prop({ mutable: true }) public value?: Value

  /**
   * True if `<qds-input>` will be validated when the form is submitted; false
   * otherwise.
   *
   * @readonly
   * @webnative
   */
  @Prop() public readonly willValidate: ElementInternals['willValidate'] = false

  @AttachInternals() private readonly internals!: ElementInternals

  /**
   * Emitted when the input loses focus.
   */
  @Event({ eventName: 'qdsBlur', bubbles: false, cancelable: false })
  private readonly blurEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when the user cancels the file picker via the <kbd>Esc</kbd> key
   * or the cancel button and when the user re-selects the same files that were
   * previously selected.
   */
  @Event({ eventName: 'qdsCancel', cancelable: false })
  private readonly cancelEmitter!: EventEmitter<void>

  /** Emitted when the user performs a search (Enter, clear, or search button). */
  @Event({ eventName: 'qdsSearch', cancelable: false })
  private readonly searchEmitter!: EventEmitter<void>

  /**
   * Emitted when an alteration to the input's value is committed by the user.
   */
  @Event({ eventName: 'qdsChange', cancelable: false })
  private readonly changeEmitter!: EventEmitter<void>

  /**
   * Emitted when the input gains focus.
   */
  @Event({ eventName: 'qdsFocus', bubbles: false, cancelable: false })
  private readonly focusEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when the input's value changes.
   */
  @Event({ eventName: 'qdsInput', cancelable: false })
  private readonly inputEmitter!: EventEmitter<QdsInputEventDetail>

  @Element() private readonly host!: HTMLElement

  @State() private tabIndex?: number

  @State() private isPasswordVisible = false

  #inheritedAttributes: Attributes = {}

  #input?: HTMLInputElement

  get #computedDisabled(): boolean {
    return this.host.matches(':disabled') || (this.disabled ?? false)
  }

  get #computedIconName():
    | `input-${InputType}`
    | `input-password-hide`
    | `input-password-show`
    | undefined {
    if (this.type === 'password')
      return this.isPasswordVisible
        ? 'input-password-hide'
        : 'input-password-show'

    switch (this.type) {
      case 'date':
      case 'datetime-local':
      case 'month':
      case 'search':
      case 'time':
      case 'week': {
        return `input-${this.type}`
      }
      default: {
        return undefined
      }
    }
  }

  get #computedSize(): Size {
    switch (this.size) {
      case 'standard':
      case 'small':
      case 'large': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  get #computedValue(): Parameters<ElementInternals['setFormValue']>[0] {
    if (typeof this.value === 'number') return this.value.toString()
    // eslint-disable-next-line unicorn/no-null
    return this.value === undefined ? null : this.value
  }

  get #hasInteractiveIcon(): boolean {
    return (
      typeof this.type === 'string' &&
      ['month', 'time', 'date', 'datetime-local', 'week', 'password'].includes(
        this.type,
      )
    )
  }

  get #iconClickHandler(): () => void {
    return this.type === 'password'
      ? this.#togglePasswordVisibility
      : this.showPicker
  }

  get #computedType(): InputType {
    return this.type === 'password' && this.isPasswordVisible
      ? 'text'
      : (this.type ?? 'text')
  }

  get #computedInteractiveIconAriaLabel(): string {
    return this.type === 'password'
      ? this.isPasswordVisible
        ? 'Hide password'
        : 'Show password'
      : (this.type ?? '')
  }

  @Listen('click')
  protected onClick(event: MouseEvent): void {
    if (this.#computedDisabled) event.stopImmediatePropagation()
  }

  @Listen('keydown')
  protected onKeydown(event: KeyboardEvent): void {
    if (this.#computedDisabled) {
      event.stopImmediatePropagation()
      return
    }

    if (event.key === 'Enter' && this.internals.form) {
      this.internals.form.requestSubmit()
      event.preventDefault()
    }

    if (event.key === 'Enter' && !supportsSearchEvent()) {
      this.searchEmitter.emit()
    }
  }

  @Watch('aria-label')
  protected ariaLabelChanged(newValue: string | null): void {
    invariant(this.#input)
    this.#input.ariaLabel =
      newValue ??
      [...this.internals.labels]
        .map((node: Node) =>
          node.textContent === ''
            ? this.host.parentElement?.querySelector('qds-label')?.text
            : node.textContent?.trim(),
        )
        .join(' ')
  }

  @Watch('disabled')
  protected disabledChanged(): void {
    if (this.#computedDisabled)
      // eslint-disable-next-line unicorn/no-null
      this.internals.setFormValue(null)
    else this.internals.setFormValue(this.#computedValue)
  }

  @Watch('selectionDirection')
  protected selectionDirectionChanged(): void {
    invariant(this.#input)

    this.#input.selectionDirection = this.selectionDirection
  }

  @Watch('selectionEnd')
  protected selectionEndChanged(): void {
    invariant(this.#input)

    this.#input.selectionEnd = this.selectionEnd
  }

  @Watch('selectionStart')
  protected selectionStartChanged(): void {
    invariant(this.#input)

    this.#input.selectionStart = this.selectionStart
  }

  @Watch('tabindex')
  protected tabindexChanged(newValue: string): void {
    const parsedValue = Number.parseInt(newValue, 10)
    this.tabIndex =
      parsedValue === 0 || Number.isNaN(parsedValue) ? undefined : parsedValue
  }

  @Watch('max')
  @Watch('maxLength')
  @Watch('min')
  @Watch('minLength')
  @Watch('pattern')
  @Watch('required')
  @Watch('step')
  @Watch('type')
  @Watch('value')
  protected validationChanged(): void {
    if (!this.#input) return

    if (this.#input.max !== this.max && this.max !== undefined)
      this.#input.max = this.max.toString()
    if (this.maxLength !== undefined) this.#input.maxLength = this.maxLength
    if (this.#input.min !== this.min && this.min !== undefined)
      this.#input.min = this.min.toString()
    if (this.minLength !== undefined) this.#input.minLength = this.minLength
    if (this.pattern !== undefined) this.#input.pattern = this.pattern
    this.#input.required = this.required ?? false
    if (this.#input.step !== this.step && this.step !== undefined)
      this.#input.step = this.step.toString()
    this.#input.type = this.#computedType

    // eslint-disable-next-line unicorn/no-null
    if (this.type !== 'file' && this.value != null)
      this.#input.value = this.value.toString()

    const { valid } = this.#input.validity
    if (valid)
      this.internals.setValidity(NO_ERROR_FLAGS, undefined, this.#input)
    else
      this.internals.setValidity(
        this.#input.validity,
        this.#input.validationMessage,
        this.#input,
      )
  }

  @Watch('value')
  protected valueChanged(): void {
    if (!this.#computedDisabled)
      this.internals.setFormValue(this.#computedValue)

    if (
      (this.#computedValue === null || this.#computedValue === '') &&
      this.type === 'file'
    ) {
      this.#clearValue()
    }
  }

  public componentWillLoad(): void {
    this.#defineGetter('form', () => this.internals.form)
    this.#defineGetter('willValidate', () => this.internals.willValidate)
    this.#defineGetter('validity', () => this.internals.validity)
    this.#defineGetter(
      'validationMessage',
      () => this.internals.validationMessage,
    )
    this.#defineGetter('files', this.#getFiles)
    this.#defineGetter('labels', () => this.internals.labels)

    this.#inheritedAttributes = inheritAriaAttributes(this.host)
    this.tabindexChanged(this.host.getAttribute('tabindex') ?? '0')
  }

  public componentDidLoad(): void {
    invariant(this.#input)

    this.ariaLabelChanged(this.host.ariaLabel)
    this.validationChanged()
    this.valueChanged()
    // FIXME: This event is supported by the native `<input>` element but not
    // recognized by Stencil's type definitions, so it needs to be manually
    // bound here.
    this.#input.addEventListener('cancel', this.#onCancel)
    this.#input.addEventListener('search', this.#onSearch)
  }

  public disconnectedCallback(): void {
    if (this.#input) {
      this.#input.removeEventListener('cancel', this.#onCancel)
      this.#input.removeEventListener('search', this.#onSearch)
    }
  }

  public render() {
    const hasValue =
      this.value !== null && this.value !== undefined && this.value !== ''
    const isSearchType = this.type === 'search'
    const shouldShowClearButton = isSearchType && hasValue
    const shouldShowSearchButton = isSearchType && this.noIcon === false
    const shouldHideIcon = !isSearchType && this.noIcon === true
    const shouldRemoveSearchPadding = isSearchType && this.noIcon === true

    return (
      <div class="qds-container">
        <input
          accept={this.accept}
          autoCapitalize={this.host.autocapitalize}
          autoComplete={this.autoComplete}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={this.host.autofocus}
          capture={this.capture}
          class={{
            'qds-search-padding': shouldRemoveSearchPadding,
            'qds-input': true,
            'qds-hide': shouldHideIcon,
            'qds-padding': shouldHideIcon,
            'qds-password-padding':
              this.type === 'password' ||
              (this.isPasswordVisible && this.type === 'text'),
          }}
          data-size={this.#computedSize}
          disabled={this.disabled}
          enterKeyHint={this.enterkeyhint}
          inputMode={this.inputmode}
          max={this.max}
          maxLength={this.maxLength}
          min={this.min}
          minLength={this.minLength}
          multiple={this.multiple}
          onBlur={this.#onBlur}
          onChange={this.#onChange}
          onFocus={this.#onFocus}
          onInput={this.#onInput}
          placeholder={this.placeholder}
          ref={this.#ref}
          spellcheck={this.host.spellcheck}
          step={this.step}
          tabIndex={this.#computedDisabled ? undefined : this.tabIndex}
          type={this.#computedType}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...this.#inheritedAttributes}
        />
        <div class="qds-focus-ring" />
        {this.#computedIconName && (
          <div
            class={{
              'qds-icon-container': true,
              'no-padding': shouldRemoveSearchPadding,
            }}
          >
            {shouldShowClearButton && (
              <qds-mini-button
                class="qds-hide"
                text="clear input"
                library="core"
                name="clear"
                disabled={this.disabled}
                onClick={this.#onClear}
                tabindex={-1}
                onQdsFocus={stopImmediatePropagation}
              />
            )}
            {this.#hasInteractiveIcon && (
              <qds-mini-button
                disabled={this.disabled}
                onClick={this.#iconClickHandler}
                text={this.#computedInteractiveIconAriaLabel}
                library="core"
                name={this.#computedIconName}
                tabindex={-1}
                onQdsFocus={stopImmediatePropagation}
              />
            )}
            {shouldShowSearchButton ? (
              <qds-mini-button
                text="search"
                library="core"
                name={this.#computedIconName}
                disabled={this.disabled}
                onClick={this.#onSearch}
                tabindex={-1}
                onQdsFocus={stopImmediatePropagation}
              />
            ) : (
              <div />
            )}
          </div>
        )}
      </div>
    )
  }

  public checkValidity: ElementInternals['checkValidity'] = () =>
    this.internals.checkValidity()

  public reportValidity: ElementInternals['reportValidity'] = () =>
    this.internals.reportValidity()

  public setCustomValidity: HTMLInputElement['setCustomValidity'] = (error) => {
    invariant(this.#input)

    this.#input.setCustomValidity(error)
    if (error)
      this.internals.setValidity(CUSTOM_ERROR_FLAGS, error, this.#input)
    else this.internals.setValidity(NO_ERROR_FLAGS)
  }

  public select: HTMLInputElement['select'] = () => {
    invariant(this.#input)

    this.#input.select()
  }

  public getSelectionDirection: () => HTMLInputElement['selectionDirection'] =
    () => {
      invariant(this.#input)

      return this.#input.selectionDirection
    }

  public getSelectionStart: () => HTMLInputElement['selectionStart'] = () => {
    invariant(this.#input)

    return this.#input.selectionStart
  }

  public getSelectionEnd: () => HTMLInputElement['selectionEnd'] = () => {
    invariant(this.#input)

    return this.#input.selectionEnd
  }

  public setRangeText = (
    replacement: string,
    start: number,
    end: number,
    selectionMode?: SelectionMode,
  ): void => {
    invariant(this.#input)

    this.#input.setRangeText(replacement, start, end, selectionMode)

    if (this.value !== this.#input.value) {
      this.value = this.#input.value
      this.inputEmitter.emit()
      this.changeEmitter.emit()
    }
  }

  public setSelectionRange: HTMLInputElement['setSelectionRange'] = (
    start,
    end,
    direction,
  ) => {
    invariant(this.#input)

    this.#input.setSelectionRange(start, end, direction)
  }

  public stepDown: HTMLInputElement['stepDown'] = (n) => {
    invariant(this.#input)

    this.#input.stepDown(n)
  }

  public stepUp: HTMLInputElement['stepUp'] = (n) => {
    invariant(this.#input)

    this.#input.stepUp(n)
  }

  public showPicker: HTMLInputElement['showPicker'] = () => {
    invariant(this.#input)

    this.#input.showPicker()
  }

  readonly #togglePasswordVisibility = (): void => {
    invariant(this.#input)
    this.isPasswordVisible = !this.isPasswordVisible

    this.#input.type = this.#computedType
  }

  readonly #ref = (input?: HTMLInputElement): void => {
    this.#input = input
  }

  readonly #getFiles: () => HTMLInputElement['files'] = () => {
    invariant(this.#input)

    return this.#input.files
  }

  readonly #onBlur = (event: FocusEvent): void => {
    this.blurEmitter.emit(pickFocusEventAttributes(event))
  }

  readonly #onCancel = (): void => {
    this.cancelEmitter.emit()
  }

  readonly #onSearch = (): void => {
    this.searchEmitter.emit()
  }

  readonly #onChange = (): void => {
    this.changeEmitter.emit()
  }

  readonly #onClear = (): void => {
    this.#clearValue()
    this.#onInput(new InputEvent('input', { inputType: 'deleteContent' }))
    invariant(this.#input)
    if (document.activeElement !== this.#input) {
      this.#input.focus()
    }
    this.searchEmitter.emit()
  }

  readonly #onFocus = (event: FocusEvent): void => {
    this.focusEmitter.emit(pickFocusEventAttributes(event))
  }

  readonly #onInput = (event: InputEvent): void => {
    invariant(this.#input)

    this.value = this.#input.value
    this.inputEmitter.emit(pickInputEventAttributes(event))
  }

  #defineGetter(p: PropertyKey, get: () => unknown): void {
    Object.defineProperty(this.host, p, { enumerable: true, get })
  }

  #clearValue(): void {
    invariant(this.#input)

    this.#input.value = ''
  }
}
